/**
 * User Management Screen Mobile
 * 
 * Mobile version of the user management interface for managers.
 * Optimized for mobile devices with touch-friendly interface.
 * 
 * @module UserManagementScreenMobile
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Search,
  Filter,
  UserPlus,
  RefreshCw,
  MoreVertical,
  Users,
  UserCheck,
  UserX
} from 'lucide-react';
import { useUserManagement } from '../../hooks/useUserManagement';
import { useAuth } from '../../contexts/AuthContext';
import { UserForm } from '../../components/manager/UserForm';
import { ConfirmationModal } from '../../components/common/ConfirmationModal';
import { UserManagementService } from '../../services/UserManagementService';
import { UserProfile, UserFilters, UserRole, CreateUserData, UpdateUserData } from '../../@types/user';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const MobileToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const SearchSection = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 12px;
  font-size: ${Typography.fontSize.md};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${Spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${ThemeColors.text.secondary};
  pointer-events: none;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  justify-content: space-between;
`;

const MobileButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  border: none;
  border-radius: 12px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover {
      background: ${ThemeColors.primary.dark};
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.main};
    
    &:hover {
    background: ${ThemeColors.background.secondary};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  overflow-x: auto;
  padding: ${Spacing.xs} 0;
`;

const FilterChip = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.active ? ThemeColors.primary.light : 'white'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.primary};
  border-radius: 20px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    color: ${ThemeColors.primary.main};
  }
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.md};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${Typography.fontWeight.bold};
  font-size: ${Typography.fontSize.md};
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
`;

const UserEmail = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const UserMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
`;

const MetaBadge = styled.span<{ variant?: 'role' | 'status' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 16px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};

  ${props => {
    if (props.variant === 'role') {
        return `
        background: ${ThemeColors.info.light};
        color: ${ThemeColors.info.dark};
      `;
    } else {
        return `
        background: ${ThemeColors.success.light};
        color: ${ThemeColors.success.dark};
        `;
    }
  }}
`;

const UserActions = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.sm};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  border-radius: 8px;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.primary.main};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl} ${Spacing.lg};
  color: ${ThemeColors.text.secondary};
`;

const LoadingCard = styled(UserCard)`
  background: linear-gradient(
    90deg,
    ${ThemeColors.background.secondary} 25%,
    ${ThemeColors.background.default} 50%,
    ${ThemeColors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ErrorMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: 12px;
  margin-bottom: ${Spacing.lg};
`;

const SuccessMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.dark};
  border-radius: 12px;
  margin-bottom: ${Spacing.lg};
`;

/**
 * Get user initials for avatar
 */
const getUserInitials = (user: UserProfile): string => {
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
};

/**
 * Format last login for display
 */
const formatLastLogin = (date: Date | string | Timestamp | undefined): string => {
  if (!date) return 'Never';
  let d: Date;
  if (date instanceof Timestamp) {
    d = date.toDate();
  } else if (typeof date === 'string') {
    d = new Date(date);
  } else {
    d = date;
  }
  const now = new Date();
  const diffInHours = (now.getTime() - d.getTime()) / (1000 * 60 * 60);
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * User Management Screen Mobile Component
 * 
 * Mobile-optimized user management interface
 */
export const UserManagementScreenMobile: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Use user management hook
  const {
    users,
    userStatistics,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    clearError,
    refreshUsers
  } = useUserManagement();

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stores, setStores] = useState<Array<{ id: string; name: string; customerNo: string | number; salesGroup?: string }>>([]);
  const [salesGroups, setSalesGroups] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);

  /**
   * Load stores on component mount
   */
  useEffect(() => {
    const loadStores = async () => {
      try {
        const storesRef = collection(db, 'Stores');
        const storesSnapshot = await getDocs(storesRef);
        const storesList = storesSnapshot.docs.map(doc => {
          const data = doc.data() as any;
          return {
            id: doc.id,
            name: data.name || data.StoreName || 'Unnamed Store',
            customerNo: data.CustomerNo || data.customerNo || '', // Check both CustomerNo (Firestore) and customerNo
            salesGroup: data.SalesGroup || data.salesGroup || ''
          };
        });
        setStores(storesList);
        
        // Extract unique sales groups
        const uniqueSalesGroups = Array.from(
          new Set(
            storesList
              .map(store => store.salesGroup)
              .filter((sg): sg is string => !!sg && sg.trim() !== '')
          )
        ).sort();
        setSalesGroups(uniqueSalesGroups);
      } catch (error) {
        console.error('Error loading stores:', error);
      }
    };
    
    loadStores();
  }, []);

  /**
   * Load all unique roles from database (excluding admin)
   */
  useEffect(() => {
    const loadAvailableRoles = async () => {
      try {
        // Fetch all users to get unique roles
        const { users: allUsers } = await UserManagementService.getUsers({}, undefined, 1000);
        const uniqueRoles = Array.from(
          new Set(allUsers.map(user => user.role).filter(role => role !== 'admin'))
        ) as UserRole[];
        setAvailableRoles(uniqueRoles.sort());
      } catch (error) {
        console.error('Error loading available roles:', error);
        // Fallback: extract from currently loaded users
        if (users && users.length > 0) {
          const uniqueRoles = Array.from(
            new Set(users.map(user => user.role).filter(role => role !== 'admin'))
          ) as UserRole[];
          setAvailableRoles(uniqueRoles.sort());
        }
      }
    };
    
    loadAvailableRoles();
  }, []); // Only run once on mount

  /**
   * Also update roles when users change (as fallback)
   */
  useEffect(() => {
    if (users && users.length > 0 && availableRoles.length === 0) {
      const uniqueRoles = Array.from(
        new Set(users.map(user => user.role).filter(role => role !== 'admin'))
      ) as UserRole[];
      if (uniqueRoles.length > 0) {
        setAvailableRoles(uniqueRoles.sort());
      }
    }
  }, [users, availableRoles.length]);

  /**
   * Load users on component mount and when filters change
   */
  useEffect(() => {
    const filters: UserFilters = {};
    
    if (selectedRole !== 'all') {
      filters.role = selectedRole;
    }

    if (searchTerm) {
      filters.searchTerm = searchTerm;
    }

    fetchUsers(filters, { field: 'firstName', direction: 'asc' }, true);
  }, [selectedRole, searchTerm, fetchUsers]);

  /**
   * Clear success message after 5 seconds
   */
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Handle user creation
   */
  const handleCreateUser = useCallback(async (userData: CreateUserData) => {
    const result = await createUser(userData);
    if (result.success) {
      setShowForm(false);
      setSuccessMessage('User created successfully!');
    } else {
      throw new Error(result.error || 'Failed to create user');
    }
  }, [createUser]);

  /**
   * Handle user editing
   */
  const handleEditUser = useCallback(async (userData: UpdateUserData) => {
    if (!editingUser) return;

    const result = await updateUser(editingUser.id, userData);
    if (result.success) {
      setShowForm(false);
      setEditingUser(undefined);
      setSuccessMessage('User updated successfully!');
    } else {
      throw new Error(result.error || 'Failed to update user');
    }
  }, [updateUser, editingUser]);

  // Confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);

  /**
   * Handle user deletion - show confirmation modal
   */
  const handleDeleteUserClick = useCallback((userToDelete: UserProfile) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirm(true);
  }, []);

  /**
   * Confirm user deletion
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;
    
    const result = await deleteUser(userToDelete.id);
    setShowDeleteConfirm(false);
    setUserToDelete(null);
    
    if (result.success) {
      setSuccessMessage('User deleted successfully!');
    }
  }, [userToDelete, deleteUser]);

  /**
   * Handle password reset
   */
  const handleResetPassword = useCallback(async (userToReset: UserProfile) => {
    const result = await resetUserPassword(userToReset.id);
    if (result.success) {
      setSuccessMessage('Password reset email sent successfully!');
    }
  }, [resetUserPassword]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    refreshUsers();
  }, [refreshUsers]);

  /**
   * Open create user form
   */
  const openCreateForm = useCallback(() => {
    setEditingUser(undefined);
    setShowForm(true);
  }, []);

  /**
   * Open edit user form
   */
  const openEditForm = useCallback((userToEdit: UserProfile) => {
    setEditingUser(userToEdit);
    setShowForm(true);
  }, []);

  /**
   * Close form
   */
  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditingUser(undefined);
  }, []);

  /**
   * Handle form submission - wrapper to handle both create and edit
   */
  const handleFormSubmit = useCallback(async (data: CreateUserData | UpdateUserData) => {
    if (editingUser) {
      await handleEditUser(data as UpdateUserData);
    } else {
      await handleCreateUser(data as CreateUserData);
    }
  }, [editingUser, handleCreateUser, handleEditUser]);

    return (
    <PageContainer isMenuCollapsed={true}>
      <MainContent>
        <Header>
          <Title>{t('storeDetails.userManagement')}</Title>
        </Header>

        {/* Success Message */}
        {successMessage && (
          <SuccessMessage>
            {successMessage}
            <MobileButton variant="secondary" onClick={() => setSuccessMessage(null)} style={{ marginTop: Spacing.sm }}>
              {t('common.close')}
            </MobileButton>
          </SuccessMessage>
        )}

      {/* Error Display */}
      {error && (
          <ErrorMessage>
          {error}
            <MobileButton variant="secondary" onClick={clearError} style={{ marginTop: Spacing.sm }}>
              {t('common.close')}
            </MobileButton>
          </ErrorMessage>
        )}

        {/* Mobile Toolbar */}
        <MobileToolbar>
          <SearchSection>
            <SearchIcon>
              <Search size={20} />
            </SearchIcon>
          <SearchInput
            type="text"
            placeholder={t('storeDetails.searchUsers')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          </SearchSection>

          <ActionButtons>
            <MobileButton variant="secondary" onClick={handleRefresh}>
              <RefreshCw />
              {t('common.refresh')}
            </MobileButton>
            <MobileButton variant="primary" onClick={openCreateForm}>
              <UserPlus />
              {t('storeDetails.addUser')}
            </MobileButton>
          </ActionButtons>

          <FilterSection>
            <FilterChip
              active={selectedRole === 'all'}
              onClick={() => setSelectedRole('all')}
            >
              All Roles
            </FilterChip>
            {availableRoles.map(role => {
              const roleLabels: Record<UserRole, string> = {
                'manager': 'Managers',
                'sales-representative': 'Sales Reps',
                'hand-lead': 'Hand Lead',
                'admin': 'Admins' // This won't be shown since we filter it out
              };
              return (
                <FilterChip
                  key={role}
                  active={selectedRole === role}
                  onClick={() => setSelectedRole(role)}
                >
                  {roleLabels[role] || role}
                </FilterChip>
              );
            })}
          </FilterSection>
        </MobileToolbar>

        {/* Users List */}
        <UsersList>
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <LoadingCard key={index}>
                <div style={{ height: '100px' }} />
              </LoadingCard>
            ))
          ) : users.length === 0 ? (
            <EmptyState>
              <Users size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
              <p>No users found</p>
            </EmptyState>
          ) : (
            users.map((user) => (
              <UserCard key={user.id}>
                <UserHeader>
                  <UserInfo>
                    <Avatar>
                      {getUserInitials(user)}
                    </Avatar>
                    <UserDetails>
                      <UserName>{user.displayName}</UserName>
                      <UserEmail>{user.email}</UserEmail>
                    </UserDetails>
                  </UserInfo>
                  <UserActions>
                    <ActionButton onClick={() => openEditForm(user)} title="Edit user">
                      <MoreVertical />
                    </ActionButton>
                  </UserActions>
                </UserHeader>

                <UserMeta>
                  <MetaBadge variant="role">
                    {user.role === 'sales-representative' ? 'Sales Rep' : user.role}
                  </MetaBadge>
                  <MetaBadge variant="status">
                    {user.status}
                  </MetaBadge>
                  {user.territory && (
                    <MetaBadge>
                      {user.territory}
                    </MetaBadge>
                  )}
                </UserMeta>

                <div style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                  Last login: {formatLastLogin(user.lastLoginAt)}
                </div>
              </UserCard>
            ))
          )}
        </UsersList>

        {/* User Form Modal */}
        {showForm && (
              <UserForm
                user={editingUser}
            mode={editingUser ? 'edit' : 'create'}
            isLoading={isCreating || isUpdating}
            onSubmit={handleFormSubmit}
            onCancel={closeForm}
            departments={['Sales', 'Management', 'Support']}
            salesGroups={salesGroups}
            stores={stores}
          />
        )}

        {/* Delete User Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete?.displayName || 'this user'}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
          }}
          isLoading={isDeleting}
        />
      </MainContent>
    </PageContainer>
  );
};